import {
  renderEventFileMessageContent,
  renderEventImageMessageContent,
  renderEventSystemNoticeCard,
  renderEventTextMessageContent,
  renderEventUnsupportedSystemCard,
} from './eventMessageContentParts';
import ChatImageGroupBubble from '../composer/ChatImageGroupBubble';
import NoteMessagePreview from '../notes/NoteMessagePreview';
import {
  hasMissingChatFileStorage,
  hasMissingChatImageStorage,
  resolveChatFileUnavailableText,
  resolveChatImageUnavailableText,
} from '../composer/chatAttachmentAvailability';
import { getFileVisual } from '../composer/fileIconClass';

export function createEventMessageContentRenderer({
  messageFormats,
  messageFilePath,
  selectedEventId,
  buildTimetableDetailUrl,
  isTimetableCreatedMessage,
  isTimetableUpdatedMessage,
  isTimetableRuntimeMessage,
  isSimpleAnnouncementMessage,
  isEventFinishedMessage,
  isEventMaterialSharedMessage,
  isEventImagesAddedMessage,
  isNoteMessage,
  isNoteDeleteMessage,
  renderTimetableRuntimeCard,
  renderAnnouncementCard,
  buildNoteActionNode,
  buildMaterialActionNode,
  buildAlbumActionNode,
  buildEventFinishedActionNode,
  buildMessageImageUrl,
  resolveImageProxySource,
  onOpenImage,
  onDownload,
  buildAssetUrl,
  ellipsizeFileName,
  formatExpireDateLabel,
  linkifyText,
  onMentionClick,
  hideUnsupportedSystemMessages = false,
}) {
  return function renderMessageContent(message) {
    const msg = message || {};

    if (msg.message_format === messageFormats?.IMAGE) {
      const groupedItems = Array.isArray(msg.image_group_items) && msg.image_group_items.length > 1
        ? msg.image_group_items.map((item, index) => {
          const fullSrc = item.image_url || buildMessageImageUrl(item.image_file_name, null);
          const proxySrc = typeof resolveImageProxySource === 'function'
            ? resolveImageProxySource(item, fullSrc)
            : (buildMessageImageUrl(item.image_file_name, 'proxy') || fullSrc);
          return {
            messageId: item.id,
            imageId: item.image_id,
            src: hasMissingChatImageStorage(item) ? '' : proxySrc,
            fullSrc,
            width: item.image_width,
            height: item.image_height,
            sourceMessage: item,
          };
        })
        : [];
      if (groupedItems.length > 1) {
        return (
          <>
            <ChatImageGroupBubble
              items={groupedItems}
              onOpen={(item) => onOpenImage?.(item.sourceMessage, item.fullSrc)}
              unavailableText={resolveChatImageUnavailableText(msg, '画像の保存期間が終了しました。')}
            />
            {msg.image_expires_at ? (
              <div className="download-row chat-message-content__downloadRow">
                <span className="expire-note-inline chat-message-content__metaText">
                  保存期間：{formatExpireDateLabel?.(msg.image_expires_at)}まで
                </span>
              </div>
            ) : null}
          </>
        );
      }

      const missingImageStorage = hasMissingChatImageStorage(msg);
      const fullSrc = msg.image_url || buildMessageImageUrl(msg.image_file_name, null);
      const proxySrc = typeof resolveImageProxySource === 'function'
        ? resolveImageProxySource(msg, fullSrc)
        : (buildMessageImageUrl(msg.image_file_name, 'proxy') || fullSrc);
      const hasImgSrc = Boolean(proxySrc) && !missingImageStorage;
      return renderEventImageMessageContent({
        hasImageSource: hasImgSrc,
        imageSource: proxySrc,
        imageWidth: msg.image_width,
        imageHeight: msg.image_height,
        onOpenImage: () => onOpenImage?.(msg, fullSrc),
        onDownloadImage: onDownload,
        imageId: msg.image_id,
        fullImageSource: fullSrc,
        imageExpiresAt: msg.image_expires_at,
        formatExpireDateLabel,
        imageUnavailableText: resolveChatImageUnavailableText(
          msg,
          '画像の保存期間が終了しました。',
        ),
      });
    }

    if (msg.message_format === messageFormats?.FILE) {
      const missingFileStorage = hasMissingChatFileStorage(msg);
      const href = msg.file_url || buildAssetUrl(messageFilePath, msg.file_file_name);
      const fileName = msg.original_name || msg.file_file_name || 'ファイル';
      const displayName = ellipsizeFileName(fileName);
      const fileVisual = getFileVisual(fileName);
      const canDownload = !missingFileStorage && Boolean(msg.file_id || href);
      return renderEventFileMessageContent({
        canDownload,
        iconClass: fileVisual.iconClass,
        visualKind: fileVisual.kind,
        displayName,
        fileExpiresAt: msg.file_expires_at,
        onDownloadFile: onDownload,
        fileId: msg.file_id,
        fileUrl: href,
        formatExpireDateLabel,
        fileDisabledText: resolveChatFileUnavailableText(
          msg,
          'ファイルの保存期間が終わりました。',
        ),
      });
    }

    if (isTimetableRuntimeMessage(msg)) {
      return renderTimetableRuntimeCard(msg);
    }

    if (isEventMaterialSharedMessage?.(msg)) {
      const materialExtra =
        typeof buildMaterialActionNode === 'function' ? buildMaterialActionNode(msg) : null;
      return renderAnnouncementCard(msg, materialExtra);
    }

    if (isEventImagesAddedMessage?.(msg)) {
      const albumExtra =
        typeof buildAlbumActionNode === 'function' ? buildAlbumActionNode(msg) : null;
      return renderAnnouncementCard(msg, albumExtra);
    }

    if (isSimpleAnnouncementMessage(msg)) {
      const eventFinishedExtra =
        isEventFinishedMessage?.(msg) && typeof buildEventFinishedActionNode === 'function'
          ? buildEventFinishedActionNode(msg)
          : null;
      const isTimetableStatus =
        isTimetableCreatedMessage?.(msg) || isTimetableUpdatedMessage?.(msg);
      const timetableId = msg.timetable_id ?? msg.timetableId ?? null;
      const detailUrl =
        isTimetableStatus && typeof buildTimetableDetailUrl === 'function'
          ? buildTimetableDetailUrl({ selectedEventId, timetableId, message: msg })
          : '';
      const timetableExtra = detailUrl ? (
        <a
          className="system-message-card__link"
          href={detailUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          タイムテーブルを開く
        </a>
      ) : null;
      return renderAnnouncementCard(msg, eventFinishedExtra || timetableExtra);
    }

    if (isNoteMessage(msg)) {
      const isNoteDelete = isNoteDeleteMessage(msg);
      const noteExtra =
        !isNoteDelete && selectedEventId && typeof buildNoteActionNode === 'function'
          ? buildNoteActionNode(msg)
          : null;
      return renderAnnouncementCard(msg, noteExtra, {
        bodyExtra: <NoteMessagePreview message={msg} />,
      });
    }

    if (msg.message_format === 'system_notice') {
      return renderEventSystemNoticeCard(renderAnnouncementCard, msg);
    }

    if (msg.message_type === 'system_message') {
      if (hideUnsupportedSystemMessages) return null;
      return renderEventUnsupportedSystemCard(renderAnnouncementCard, msg);
    }

    return renderEventTextMessageContent(linkifyText, msg.content, msg.mentions, onMentionClick);
  };
}
